import { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import axios from 'axios';
import { BarrioContext } from '../components/BarrioContext';
import { useAuth } from '../components/AuthContext';
import { useTerminologia } from '../hooks/useTerminologia';
import LoadingOverlay from '../components/LoadingOverlay';
import SinAccesoResidente from '../components/SinAccesoResidente';
import { usePersonaResidente } from '../components/PersonaResidenteContext';

const formatMoney = (n) =>
    new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(n ?? 0);

const formatPeriodo = (p) => {
    if (!p) return '';
    const d = new Date(p);
    const label = d.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });
    return label.charAt(0).toUpperCase() + label.slice(1);
};

const ESTADO_COLORS = {
    VIGENTE:  { bg: '#eff8ff', color: '#1570ef', border: '#b2ddff' },
    PAGADO:   { bg: '#ecfdf3', color: '#12b76a', border: '#a9efc5' },
    VENCIDO:  { bg: '#fef3f2', color: '#f04438', border: '#fecdca' },
    PENDIENTE: { bg: '#fffaeb', color: '#b54708', border: '#fedf89' },
};

const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s;

const estadoBadge = (row) => {
    const estado = row.estado || '—';
    const s = ESTADO_COLORS[estado.trim().toUpperCase()] || { bg: '#f2f4f7', color: '#667085', border: '#d0d5dd' };
    return (
        <span style={{
            background: s.bg, color: s.color, border: `1px solid ${s.border}`,
            fontWeight: 600, fontSize: '0.75rem', padding: '0.15rem 0.6rem', borderRadius: 99,
        }}>
            {capitalize(estado)}
        </span>
    );
};

// Cuotas de plan de pago: "Pendiente" se muestra como "Vigente" (celeste), "Pagada" en verde.
const CUOTA_ESTADO_MAP = {
    PENDIENTE: { label: 'Vigente', bg: '#eff8ff', color: '#1570ef', border: '#b2ddff' },
    PAGADA:    { label: 'Pagada',  bg: '#ecfdf3', color: '#12b76a', border: '#a9efc5' },
};

const cuotaEstadoBadge = (row) => {
    const estado = (row.estado || '').trim();
    const s = CUOTA_ESTADO_MAP[estado.toUpperCase()];
    if (!s) {
        return (
            <span style={{
                background: '#f2f4f7', color: '#667085',
                fontWeight: 600, fontSize: '0.75rem', padding: '0.15rem 0.6rem', borderRadius: 99,
            }}>
                {estado || '—'}
            </span>
        );
    }
    return (
        <span style={{
            background: s.bg, color: s.color, border: `1px solid ${s.border}`,
            fontWeight: 600, fontSize: '0.75rem', padding: '0.15rem 0.6rem', borderRadius: 99,
        }}>
            {s.label}
        </span>
    );
};

const EstadoCuenta = () => {
    const HOST_API = process.env.REACT_APP_URL;
    const { barrio } = useContext(BarrioContext);
    const { administracionId } = useAuth();
    const { sinAcceso } = usePersonaResidente();
    const t = useTerminologia();

    const usuarioId = (() => { try { const u = JSON.parse(localStorage.getItem('user') || '{}'); return u.id || u.usuarioId || 0; } catch { return 0; } })();

    const [ufs, setUfs] = useState([]);
    const [ufSel, setUfSel] = useState(null);
    const [historial, setHistorial] = useState([]);
    const [cuotas, setCuotas] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!barrio?.id || !usuarioId) return;
        axios.get(`${HOST_API}api/unidadesFuncionales/GetUFsByUsuario?usuarioId=${usuarioId}&barrioId=${barrio.id}`)
            .then(({ data }) => {
                setUfs(data ?? []);
                setUfSel((prev) => prev ?? (data?.[0]?.unidadFuncionalId ?? null));
            })
            .catch(() => {});
    }, [barrio?.id, usuarioId, HOST_API]);

    const fetchHistorial = useCallback(async () => {
        if (!barrio?.id || !ufSel) return;
        setLoading(true);
        try {
            const { data } = await axios.get(`${HOST_API}api/estadoCuentas/GetEstadoCuenta`, {
                params: { barrioId: barrio.id, unidadFuncionalId: ufSel, administracionId },
            });
            setHistorial(data?.historial ?? []);
            setCuotas(data?.cuotas ?? []);
        } catch {
            setHistorial([]);
            setCuotas([]);
        } finally {
            setLoading(false);
        }
    }, [barrio?.id, ufSel, administracionId, HOST_API]);

    useEffect(() => { fetchHistorial(); }, [fetchHistorial]);

    const ufLabel = useMemo(() => ufs.find(u => u.unidadFuncionalId === ufSel)?.unidadFuncional ?? '', [ufs, ufSel]);

    if (sinAcceso) return <SinAccesoResidente />;

    return (
        <>
            <h4>Estado de Cuenta</h4>
            {ufLabel && <p style={{ color: '#667085', fontSize: '0.9rem', marginTop: '-0.5rem' }}>{t.UnidadFuncional}: {ufLabel}</p>}
            <br />

            {ufs.length > 1 && (
                <div className="flex align-items-center gap-3 mb-4">
                    <Dropdown
                        value={ufSel}
                        options={ufs}
                        optionLabel="unidadFuncional"
                        optionValue="unidadFuncionalId"
                        onChange={(e) => setUfSel(e.value)}
                        className="p-inputtext-sm"
                        style={{ minWidth: 220 }}
                    />
                </div>
            )}

            <LoadingOverlay visible={loading} />
            {!loading && (
                <div className="grid">
                    <div className="col-12">
                        <h5>Expensas</h5>
                        <DataTable
                            value={historial}
                            paginator
                            rows={12}
                            rowsPerPageOptions={[12, 24, 50]}
                            responsiveLayout="scroll"
                            emptyMessage="No hay liquidaciones registradas para esta unidad"
                            sortField="periodo"
                            sortOrder={-1}
                            style={{ fontSize: '0.88rem' }}
                        >
                            <Column field="periodo" header="Período" sortable body={(r) => formatPeriodo(r.periodo)} style={{ minWidth: 150 }} />
                            <Column field="saldoDeudor" header="Saldo Deudor" sortable body={(r) => formatMoney(r.saldoDeudor)} style={{ minWidth: 130 }} />
                            <Column field="intereses" header="Intereses" sortable body={(r) => formatMoney(r.intereses)} style={{ minWidth: 120 }} />
                            <Column field="expensaMes" header="Expensa del mes" sortable body={(r) => formatMoney(r.expensaMes)} style={{ minWidth: 140 }} />
                            <Column field="recargoSegundoVencimiento" header="Recargo 2do Vencimiento" sortable body={(r) => formatMoney(r.recargoSegundoVencimiento)} style={{ minWidth: 150 }} />
                            <Column field="descuentos" header="Descuentos" sortable body={(r) => formatMoney(r.descuentos)} style={{ minWidth: 130 }} />
                            <Column field="pagosRealizados" header="Pagos realizados" sortable body={(r) => formatMoney(r.pagosRealizados)} style={{ minWidth: 150 }} />
                            <Column
                                field="saldoFinal"
                                header="Saldo Final"
                                sortable
                                body={(r) => (
                                    <span style={{ fontWeight: 700, color: (r.saldoFinal ?? 0) <= 0 ? '#12b76a' : '#f04438' }}>
                                        {formatMoney(r.saldoFinal)}
                                    </span>
                                )}
                                style={{ minWidth: 130 }}
                            />
                            <Column field="estado" header="Estado" body={estadoBadge} style={{ minWidth: 110 }} />
                        </DataTable>
                    </div>

                    <div className="col-12">
                        <h5>Cuotas de plan de pago</h5>
                        <DataTable
                            value={cuotas}
                            paginator
                            rows={12}
                            rowsPerPageOptions={[12, 24, 50]}
                            responsiveLayout="scroll"
                            emptyMessage="No hay cuotas de plan de pago registradas para esta unidad"
                            sortField="periodo"
                            sortOrder={-1}
                            style={{ fontSize: '0.88rem' }}
                        >
                            <Column field="periodo" header="Período" sortable body={(r) => formatPeriodo(r.periodo)} style={{ minWidth: 150 }} />
                            <Column field="cuota" header="Cuota" sortable style={{ minWidth: 90 }} />
                            <Column field="concepto" header="Concepto" body={(r) => r.concepto || '—'} style={{ minWidth: 160 }} />
                            <Column field="importe" header="Importe" sortable body={(r) => formatMoney(r.importe)} style={{ minWidth: 130 }} />
                            <Column field="pagado" header="Pagado" sortable body={(r) => formatMoney(r.pagado)} style={{ minWidth: 130 }} />
                            <Column field="saldo" header="Saldo" sortable body={(r) => formatMoney(r.saldo)} style={{ minWidth: 130 }} />
                            <Column field="estado" header="Estado" body={cuotaEstadoBadge} style={{ minWidth: 110 }} />
                        </DataTable>
                    </div>
                </div>
            )}
        </>
    );
};

export default EstadoCuenta;
